import { z } from 'zod'
import { type PostureBand, formatPosturePercent, toPostureBand } from '../utils/threat-model/scorecard-bands.ts'
import * as styles from './scorecard-total-donut.css.ts'

const RADIUS = 42
const STROKE = 12

export const ScorecardTotalDonutSizeSchema = z.enum(['large', 'compact', 'table'])
export type ScorecardTotalDonutSize = z.infer<typeof ScorecardTotalDonutSizeSchema>

export const ScorecardValueToneSchema = z.enum(['band', 'plain'])
export type ScorecardValueTone = z.infer<typeof ScorecardValueToneSchema>

export type ScorecardTotalDonutProps = {
  /** Mean of the scores being summarised, 0–1. `undefined` when nothing could be scored. */
  score: number | undefined
  /** How many things fed the mean, for the accessible description. */
  scoredCount: number
  totalCount: number
  /**
   * What the counts count and what the score is of.
   *
   * The ring itself does not know whether it is averaging a repository's dimensions or an estate's
   * repositories, and the accessible description is the one place that difference has to be spelled out.
   * Defaults keep the repository Scorecard tab's wording, which is where this started.
   */
  unitLabel?: string
  subject?: string
  /**
   * `compact` for a ring standing where a figure's number would go, `large` when it anchors a banner,
   * `table` for a table cell, at the diameter the repositories page's risk donut uses.
   *
   * The SVG is a 0–100 viewBox, so the arc and its stroke scale with the box; the number inside keeps its
   * size at both, because it is the headline wherever the ring appears.
   */
  size?: ScorecardTotalDonutSize
  /**
   * Whether the number inside repeats the arc's colour.
   *
   * `plain` where the ring should be the only thing carrying colour language — the estate card, whose
   * figures are otherwise all primary text. Defaults to `band`, which is what the repository Scorecard
   * tab shows.
   */
  valueTone?: ScorecardValueTone
}

/**
 * The cumulative score as a ring.
 *
 * `pathLength={100}` makes the dash array *be* the percentage, so no circumference arithmetic is needed
 * and the arc cannot drift out of sync with the number printed inside it.
 */
export const ScorecardTotalDonut: React.FC<ScorecardTotalDonutProps> = ({
  score,
  scoredCount,
  totalCount,
  unitLabel = 'dimensions',
  subject = 'Cumulative score',
  size = 'large',
  valueTone = 'band',
}) => {
  const band: PostureBand = score === undefined ? 'unknown' : toPostureBand(score)
  const percent = score === undefined ? 0 : Math.round(score * 100)
  const label =
    score === undefined
      ? `No ${subject.toLowerCase()}: none of the ${totalCount} ${unitLabel} could be scored`
      : `${subject} ${percent} percent, averaged across ${scoredCount} of ${totalCount} ${unitLabel}`

  return (
    <div className={styles.donut({ size })}>
      <svg className={styles.ring} viewBox="0 0 100 100" role="img" aria-label={label}>
        <circle className={styles.track} cx="50" cy="50" r={RADIUS} strokeWidth={STROKE} fill="none" />
        {score !== undefined && (
          <circle
            className={styles.arc({ band })}
            cx="50"
            cy="50"
            r={RADIUS}
            strokeWidth={STROKE}
            fill="none"
            strokeLinecap="round"
            pathLength={100}
            strokeDasharray={`${percent} ${100 - percent}`}
            // Start at twelve o'clock; SVG's zero is at three.
            transform="rotate(-90 50 50)"
          />
        )}
      </svg>
      <p className={styles.value({ band: valueTone === 'plain' ? 'plain' : band, size })} aria-hidden>
        {score === undefined ? '—' : formatPosturePercent(score).replace('%', '')}
      </p>
    </div>
  )
}
